import {
  type FC,
  type ReactElement,
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState
} from 'react';
import {
  Responsive as ResponsiveGridLayout,
  type Layout,
  type Layouts,
  type CoreProps
} from 'react-grid-layout';
import styled, { css } from 'styled-components';

import Flex from '../../Flex';
import { useElement, useI18n } from '../../../hooks';
import { defaultThemeProp } from '../../../theme';

import useGridLayoutConfig, { type GridBreakPointWiseValues, type GridLayoutModel } from './config';
import EditorBackground from './EditorBackground';
import EditorGridItem, { ResizeHandle, resizeHandleClassName } from './EditorGridItem';
import {
  checkLayoutForGaps,
  getBreakpointAndCols,
  getRowHeight,
  useGridItemSorter,
  useMonitoredSize,
  type GridLayoutRowsProp
} from './utils';
import ReactResizableStyles from './ReactResizableStyles';
import ReactGridLayoutStyles from './ReactGridLayoutStyles';

const StyledEditor = styled(Flex)(({ theme }) => {
  const {
    base: {
      palette: { interactive }
    }
  } = theme;
  return css`
    position: relative;

    /* Needed for the background grid to be visible */
    z-index: 0;
    block-size: 100%;
    overflow-y: auto;

    /* To minimize height triggers during dragging due to horizontal scrollbar showing up */
    overflow-x: hidden;

    .react-grid-item.react-grid-placeholder {
      background: ${interactive};
      opacity: 0.3;
    }
  `;
});
StyledEditor.defaultProps = defaultThemeProp;

export interface GridLayoutHandleValue {
  /** Used to get layout model with imperative handle. Returns the layout model from all breakpoints. Returns undefined when no children. */
  getLayoutModel: () => GridLayoutModel | undefined;

  /**
   * Returns a flag indicating whether there are any gaps in the current layout.
   * Highlights all background cells to indicate the same.
   */
  checkForGaps: () => boolean;
}

/** Props for GridLayoutEditor component */
export interface GridLayoutEditorProps {
  /**
   * Called whenever there's a change in the layoutModel.
   * Change-handler of the CONTROLLED COMPONENT
   */
  onModelChange: (newModel: GridLayoutModel | undefined) => void;

  /**
   * Value of the CONTROLLED component
   * Passed when editing a previously saved layout.
   * Can be `undefined` when no children.
   * It should be the same as the one emitted by `getLayoutModel` or `handle`
   */
  layoutModel: GridLayoutModel | undefined;

  /**
   * Breakpoints set on the Grid.
   * Needs to be aligned with `rows` and `cols`
   */
  breakpoints: GridBreakPointWiseValues;

  /**
   * # of rows to fit into the container height at each breakpoint (unless a fixed rowHeight is preferred)
   * rows should be aligned with `breakpoints` prop
   */
  rows: GridLayoutRowsProp;

  /**
   * Breakpoint-wise numColumns on the Grid.
   * cols should be aligned with `breakpoints` prop
   */
  cols: GridBreakPointWiseValues;

  /**
   * If parent DOM node of ResponsiveReactGridLayout or ReactGridLayout has "transform: scale(n)" css property,
   * we should set scale coefficient to avoid render artifacts while dragging.
   */
  transformScale?: CoreProps['transformScale'];

  /** Ref to access the editor state. Used to get the current layout-model when persisting it. */
  handle?: Ref<GridLayoutHandleValue>;

  /**
   * Child elements to be rendered as Grid items.
   * Each element should have a unique `key`
   * Memoize children for a better performance: https://github.com/react-grid-layout/react-grid-layout#performance
   * Set up an `itemLabel` prop with the text to call out when the item is focused.
   * If a child needs to be initialized with a specific x/y/w/h,
   *   or if you want to specify any special properties like minW, minH, etc.,
   *   set up a `data-grid` in layoutModel prop with any subset of properties
   * The prop can be undefined on init when no elements.
   */
  children?: ReactElement[];

  /** Item label mapped with item id for a11y */
  itemLabelMap: Record<string, string>;

  /**
   * Optional minimum row height (px) to override the built-in defaults.
   * Useful when grid items contain content that requires more vertical space
   * than the default minimum (e.g. compact SimpleValue insights).
   */
  minRowHeight?: number;
}

/**
 * Editor component to create Grid Layouts
 * @component
 */
const GridLayoutEditor: FC<GridLayoutEditorProps> = ({
  handle,
  children,
  layoutModel,
  rows,
  onModelChange,
  itemLabelMap,
  minRowHeight: minRowHeightProp,
  ...restProps
}) => {
  // Track width changes: https://github.com/react-grid-layout/react-grid-layout#providing-grid-width
  const [wrapperEl, setWrapperEl] = useElement<HTMLElement>();
  const size = useMonitoredSize(wrapperEl);
  const width = size?.width;
  const height = size?.height;
  const commonGridConfig = useGridLayoutConfig(true);
  const t = useI18n();
  const { breakpoint, numColumns } = useMemo(
    () => getBreakpointAndCols(width || 0, restProps.breakpoints, restProps.cols),
    [width, restProps.breakpoints, restProps.cols]
  );
  const { rowHeight, numRowsPerPage } = useMemo(
    () =>
      getRowHeight(
        breakpoint,
        height || 0,
        commonGridConfig.margin[0],
        rows,
        layoutModel,
        minRowHeightProp
      ),
    [breakpoint, commonGridConfig.margin, height, rows, layoutModel, minRowHeightProp]
  );
  const currentLayout = useMemo(() => layoutModel?.[breakpoint], [breakpoint, layoutModel]);

  const onLayoutChange = useCallback(
    (curLayout: Layout[], allLayouts: Layouts) => {
      onModelChange(!children || children.length === 0 ? undefined : allLayouts);
    },
    [children, onModelChange]
  );

  // Callback to sort child DOM nodes to ensure an intuitive tabbing order
  const gridItemSorter = useGridItemSorter(currentLayout, numColumns);

  // Set up keyboard accessibility
  const [listEl, setListEl] = useElement<HTMLElement>();
  useEffect(() => {
    if (listEl) {
      listEl.setAttribute('role', 'list');
      listEl.setAttribute('tabindex', '0');
      listEl.setAttribute('aria-label', t('configurable_layout_instructions'));
    }
  }, [listEl, t]);

  const moveItem = useCallback(
    (itemKey: ReactElement['key'], deltaX: number, deltaY: number) => {
      // Should be initialized by this point
      if (!currentLayout) return;
      const itemIndex = currentLayout.findIndex(item => item.i === itemKey);
      // Should be present on the currentLayout by this point.
      if (itemIndex === -1) return;

      const oldItem = currentLayout[itemIndex];
      const newItem: Layout = {
        ...oldItem,
        // `Math.max` needed to avoid a glitch when moving left beyond the left edge
        x: Math.max(0, oldItem.x + deltaX),
        y: Math.max(0, oldItem.y + deltaY)
      };
      const newLayout = [
        ...currentLayout.slice(0, itemIndex),
        newItem,
        ...currentLayout.slice(itemIndex + 1)
      ];
      const newModel = { ...layoutModel, [breakpoint]: newLayout };
      onModelChange(!children || children.length === 0 ? undefined : newModel);
    },
    [breakpoint, children, currentLayout, layoutModel, onModelChange]
  );

  const addOrSubtractItemSize = useCallback(
    (itemKey: ReactElement['key'], deltaW: number, deltaH: number) => {
      // Should be initialized by this point
      if (!currentLayout) return;
      const itemIndex = currentLayout.findIndex(item => item.i === itemKey);
      // Should be present on the currentLayout by this point.
      if (itemIndex === -1) return;

      const oldItem = currentLayout[itemIndex];
      const newItem: Layout = {
        ...oldItem,
        // `Math.max` needed to avoid a glitch when moving left beyond the left edge
        w: Math.max(oldItem.minW || 2, oldItem.w + deltaW),
        h: Math.max(oldItem.minH || 2, oldItem.h + deltaH)
      };
      const newLayout = [
        ...currentLayout.slice(0, itemIndex),
        newItem,
        ...currentLayout.slice(itemIndex + 1)
      ];
      const newModel = { ...layoutModel, [breakpoint]: newLayout };
      onModelChange(!children || children.length === 0 ? undefined : newModel);
    },
    [breakpoint, children, currentLayout, layoutModel, onModelChange]
  );

  // Provide a way for the parent component to get layout-model on demand
  const [highlightedBG, setHighlightedBG] = useState(false);
  useImperativeHandle(
    handle,
    () => ({
      getLayoutModel: () => {
        // Without this check, we'll end up with the dummy model set up down below for this case
        if (!children || children.length === 0) return undefined;
        return layoutModel;
      },

      checkForGaps: () => {
        // On load when no items are added.
        if (!currentLayout) return true;
        const hasGaps = checkLayoutForGaps(currentLayout, numColumns);
        setHighlightedBG(hasGaps);
        return hasGaps;
      }
    }),
    [children, layoutModel, currentLayout, numColumns]
  );

  return (
    // Editor is always in LTR. At runtime, the layout is mirrored depending on locale.
    <StyledEditor ref={setWrapperEl} dir='ltr'>
      <ReactResizableStyles />
      <ReactGridLayoutStyles />

      {!!width && !!height && (
        <>
          <EditorBackground
            data-testid='editor'
            layout={currentLayout}
            numColumns={numColumns}
            rowHeight={rowHeight}
            rowsPerPage={numRowsPerPage}
            highlighted={highlightedBG}
          />

          <ResponsiveGridLayout
            {...commonGridConfig}
            width={width}
            rowHeight={rowHeight}
            onLayoutChange={onLayoutChange}
            resizeHandles={['s', 'e', 'n', 'w', 'sw', 'nw', 'se', 'ne']}
            resizeHandle={<ResizeHandle />}
            // To prevent drag event on the resize handles
            draggableCancel={`.${resizeHandleClassName}`}
            innerRef={setListEl}
            layouts={layoutModel}
            {...restProps}
          >
            {children?.sort(gridItemSorter).map(child => {
              const gridLayoutModel = layoutModel?.[breakpoint]?.filter(
                layout => layout.i === child.key
              );
              return (
                <EditorGridItem
                  key={child.key}
                  itemKey={child.key}
                  data-grid={gridLayoutModel && gridLayoutModel[0]}
                  itemLabel={itemLabelMap[child.key!] || ''}
                  moveItem={moveItem}
                  addOrSubtractItemSize={addOrSubtractItemSize}
                >
                  {child}
                </EditorGridItem>
              );
            })}

            {(!children || children.length === 0) && (
              // Placeholder when no children. Needed to avoid exceptions:
              // https://github.com/react-grid-layout/react-grid-layout/issues/216
              <div key='--' data-grid={{ x: 0, y: 0, w: 1, h: 1, resizeHandles: [] }} />
            )}
          </ResponsiveGridLayout>
        </>
      )}
    </StyledEditor>
  );
};

export default GridLayoutEditor;
